import { ofType, combineEpics } from "redux-observable";
import { concat, from, Observable, of } from "rxjs";
import {
  map,
  switchMap,
  concatMap,
  mergeMap,
  startWith,
  delay,
  repeat,
  filter,
  takeUntil
} from "rxjs/operators";
import { responseFrom, ResponseAction, request } from "../../ajax";
import { Action } from "../../Store";
import { ErrorResponse } from "../../utils";
import { Actions } from "./state/actions";
import { State } from "./state/state";

interface PostRequestWithKey {
  key: State.Keys;
  post: State.InputServer;
}

// These epic patterns described by react-observable maintainer here:
// https://stackoverflow.com/questions/48261438/composing-and-sequencing-multiple-epics-in-redux-observable/48307271#48307271
// This allows to run through ajax reducers.
// TODO - replace the switchMap pipe with a pipable operator so we can reuse this pattern
export const getEpic = (action$: Observable<Action>): Observable<Action> =>
  action$.pipe(
    ofType(Actions.REQUEST_GET),
    switchMap(() =>
      action$.pipe(
        responseFrom("/ATX/hardware/inputs"),
        map((a: ResponseAction<State.ServerResponse>) => {
          return Actions.getResponse({ data: a.data });
        }),
        startWith(request("/ATX/hardware/inputs"))
      )
    )
  );

export const postEpic = (action$: Observable<Action>): Observable<Action> =>
  action$.pipe(
    ofType(Actions.REQUEST_POST),
    filter((a: Actions.PostRequest) => !!Object.keys(a.data).length),
    mergeMap((a: Actions.PostRequest) =>
      concat(
        from(
          Object.keys(a.post || {}).map(key => {
            return { key, post: a.post && a.post[key] };
          })
        ).pipe(
          concatMap((req: PostRequestWithKey) =>
            action$.pipe(
              responseFrom(`/ATX/hardware/inputs/${req.key}`, "POST"),
              switchMap((a: ResponseAction<ErrorResponse>) =>
                concat(
                  of(Actions.postResponse({ data: a.data })),
                  of(Actions.getRequest())
                )
              ),
              startWith(request(`/ATX/hardware/inputs/${req.key}`, req.post))
            )
          )
        ),
        of(Actions.postResponseEnd())
      )
    )
  );

export const saveEpic = (action$: Observable<Action>): Observable<Action> =>
  action$.pipe(
    ofType(Actions.RESPONSE_POST_END),
    concatMap(() => of(request("/ATX/hardware/exe/save", { save: 1 })))
  );

export const pollEpic = (action$: Observable<Action>) =>
  action$.pipe(
    ofType(Actions.POLL_START),
    switchMap(() =>
      action$.pipe(
        ofType(Actions.RESPONSE_GET),
        delay(5000),
        map(() => Actions.getRequest()),
        repeat(),
        takeUntil(action$.pipe(ofType(Actions.POLL_STOP))),
        startWith(Actions.getRequest())
      )
    )
  );

export default combineEpics(getEpic, postEpic, saveEpic, pollEpic);
